import { PersonAvatar } from "@/components/PersonAvatar";
import { Skeleton } from "@lytra/frontend-common/components/v2/ui/skeleton";
import { cn } from "@lytra/frontend-common/components/v2/lib/utils";
import type { Colleague } from "./MentionList";

/**
 * The rows the two colleague pickers share: the @ picker of a note (`MentionList`, driven by the
 * Tiptap editor) and the recipient field of "An Kollegen senden" (`RecipientField`, a token
 * input). Only the rows are shared; each picker keeps its own keyboard and focus model.
 */

/** One row of a picker list: an option, a message row or a skeleton row. */
export const PICKER_ROW =
  "relative flex min-h-8 w-full cursor-pointer select-none items-center gap-2 rounded-md px-2 py-1.5 text-left text-body outline-hidden";

/** A row that says why nothing can be picked: disabled, never highlighted. */
export function PickerMessageRow({
  id,
  children,
}: {
  id: string;
  children: string;
}) {
  return (
    <div
      id={id}
      role="option"
      aria-selected={false}
      aria-disabled="true"
      className={cn(PICKER_ROW, "cursor-default text-ink-muted")}
    >
      {children}
    </div>
  );
}

/** The loading state: three skeleton rows, hidden from assistive technology. */
export function PickerSkeletonRows() {
  return [0, 1, 2].map((row) => (
    <div
      key={row}
      aria-hidden="true"
      className={cn(PICKER_ROW, "cursor-default")}
    >
      <Skeleton shape="circle" className="size-5" />
      <Skeleton shape="line" className="w-32" />
    </div>
  ));
}

/**
 * What an option row shows of a colleague: the avatar, the name, and the email when another
 * colleague has the same name. The name is a React text node, never markup.
 */
export function ColleagueOptionContent({
  colleague,
}: {
  colleague: Colleague;
}) {
  return (
    <>
      <PersonAvatar personId={colleague.id} name={colleague.label} size="xs" />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-body text-ink">{colleague.label}</span>
        {colleague.showEmail && colleague.email && (
          <span className="truncate text-caption text-ink-muted">
            {colleague.email}
          </span>
        )}
      </span>
    </>
  );
}
